import { utils } from 'kb-shared';
import { createURLSearchParams } from 'utilities/urlUtils';

const { getIdToken } = utils;

interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2
};

const shouldRetry = (response?: Response): boolean => {
  // Retry on network errors
  if (!response) return true;

  if (response.status === 404) return false;

  return true;
};

const calculateDelay = (attempt: number, config: RetryConfig): number => {
  const exponentialDelay = config.baseDelay * Math.pow(config.backoffMultiplier, attempt);
  const cappedDelay = Math.min(exponentialDelay, config.maxDelay);

  // Add jitter (±25% of the delay)
  const jitter = cappedDelay * 0.25 * (Math.random() * 2 - 1);
  return Math.max(0, cappedDelay + jitter);
};

const withRetry = async <T>(
  operation: () => Promise<T>,
  config: RetryConfig = DEFAULT_RETRY_CONFIG
): Promise<T> => {
  let lastError: unknown;

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      const result = await operation();
      return result;
    } catch (error) {
      lastError = error;

      if (attempt === config.maxRetries) {
        throw error;
      }

      const response = error instanceof Response ? error : undefined;
      if (!shouldRetry(response)) {
        throw error;
      }

      const delay = calculateDelay(attempt, config);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError;
};

export interface UploadFileErrorResponse {
  errors: string;
}
export interface UploadFileException {
  message: string;
}

export interface UploadFileSuccessResponse {
  file: string;
}

export type UploadFileResponse =
  | UploadFileSuccessResponse
  | UploadFileErrorResponse
  | UploadFileException;

interface FileRoute {
  upload: string;
  retrieve: string;
  delete: string;
  temporaryUpload: string;
}

const routes: FileRoute = {
  upload: `${import.meta.env.VITE_APP_GENERATIONS_URL}/pf2/patient_files/upload`,
  retrieve: `${import.meta.env.VITE_APP_GENERATIONS_URL}/pf2/patient_files/retrieve`,
  delete: `${import.meta.env.VITE_APP_GENERATIONS_URL}/pf2/patient_files/delete`,
  temporaryUpload: `${import.meta.env.VITE_APP_GENERATIONS_URL}/pf2/temporary_patient_files/upload`
};

export enum CATEGORY {
  PROFILE_PHOTO = 'profile_photo',
  INSURANCE_CARD_FRONT = 'insurance_card_front',
  INSURANCE_CARD_BACK = 'insurance_card_back',
  MESSAGE_ATTACHMENT = 'message_attachment',
  DOCUMENT = 'document',
  ID_FRONT = 'id_front'
}

export enum OTHER_ID_TYPES {
  MESSAGE_ID = 'message_id'
}

export const retrieveFile = async (category: CATEGORY): Promise<UploadFileResponse> => {
  const url = `${routes.retrieve}?category=${category}`;
  const idtoken = getIdToken();

  if (!idtoken) {
    throw Error('idtoken invalid while retrieving file');
  }

  return fetch(url, {
    method: 'GET',
    headers: { idtoken: idtoken }
  }).then(response => response.json());
};

interface FileToUpload {
  file: Blob | File;
  category: CATEGORY;
  otherId?: number;
  otherIdType?: OTHER_ID_TYPES;
  description?: string;
  fileName?: string;
  purpose?: string;
}

export const uploadFile = async (fileToUpload: FileToUpload): Promise<Response> => {
  const idtoken = getIdToken();
  if (!idtoken) {
    throw Error('idtoken invalid while uploading file');
  }

  const searchParameters = createURLSearchParams({
    category: fileToUpload.category,
    other_id: fileToUpload.otherId,
    other_id_type: fileToUpload.otherIdType,
    description: fileToUpload.description,
    purpose: fileToUpload.purpose
  });
  const url = `${routes.upload}?${searchParameters}`;

  const formData = new FormData();
  if (fileToUpload.fileName) {
    formData.append('file', fileToUpload.file, fileToUpload.fileName);
  } else {
    formData.append('file', fileToUpload.file);
  }

  return withRetry(async () => {
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: { idtoken: idtoken }
    });

    // If the response is not ok, throw it so the retry logic can handle it
    if (!response.ok) {
      throw response;
    }

    return response;
  });
};

export const uploadTemporaryFile = async (fileToUpload: FileToUpload): Promise<Response> => {
  const idtoken = getIdToken();
  if (!idtoken) {
    throw Error('idtoken invalid while uploading file');
  }

  const searchParameters = createURLSearchParams({
    category: fileToUpload.category
  });
  const url = `${routes.temporaryUpload}?${searchParameters}`;

  const formData = new FormData();
  if (fileToUpload.fileName) {
    formData.append('temporary_file', fileToUpload.file, fileToUpload.fileName);
    formData.append('file', fileToUpload.file, fileToUpload.fileName);
  } else {
    formData.append('temporary_file', fileToUpload.file);
    formData.append('file', fileToUpload.file);
  }

  return fetch(url, {
    method: 'POST',
    body: formData,
    headers: { idtoken: idtoken }
  });
};

export const deleteFile = async (id: number, category: CATEGORY) => {
  category = category || CATEGORY.DOCUMENT;
  const idtoken = getIdToken();
  if (!idtoken) {
    throw Error('idtoken invalid while deleting file');
  }
  const searchParameters = createURLSearchParams({ category, id });
  const url = `${routes.delete}?${searchParameters}`;

  return fetch(url, {
    method: 'DELETE',
    headers: { idtoken: idtoken }
  });
};
